import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Play, Lock, Clock, Video, Users, Trash2 } from 'lucide-react';

export const CourseHero = ({ course, stats, curriculum, hasAccess, canEdit, courseId, onStart, onPurchase, onDelete, isDeleting }) => (
  <div className="relative w-full h-[60vh] min-h-[420px] overflow-hidden bg-black" data-testid="course-hero">
    {course?.thumbnail_url ? (
      <img
        src={course.thumbnail_url}
        alt={course.title}
        className="absolute inset-0 w-full h-full object-cover opacity-55"
      />
    ) : (
      <div className="absolute inset-0 bg-gradient-to-br from-brand-gold/20 via-brand-blue/10 to-black" />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/40 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-r from-brand-bg/70 via-transparent to-transparent" />

    {canEdit && (
      <div className="absolute top-6 right-6 z-10 flex gap-2">
        <Link to={`/courses/${courseId}/edit`}>
          <Button variant="outline" className="bg-black/40 backdrop-blur-md border-white/20 text-white hover:bg-white/10 text-xs" data-testid="edit-course-btn">
            Edit Course
          </Button>
        </Link>
        {onDelete && (
          <Button
            variant="outline"
            onClick={onDelete}
            disabled={isDeleting}
            className="bg-black/40 backdrop-blur-md border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 text-xs px-3"
            data-testid="delete-course-btn"
            aria-label="Delete course"
            title="Delete course"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    )}

    <div className="absolute bottom-0 left-0 right-0 z-10 p-6 md:p-12 lg:p-16">
      <div className="max-w-3xl">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-brand-gold tracking-[0.2em] uppercase text-xs font-semibold">
            {course?.category || 'Masterclass'}
          </span>
          <span className="w-1 h-1 rounded-full bg-brand-gold/60" />
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
            course?.tier_required === 'founder' ? 'bg-brand-gold/90 text-black' :
            course?.tier_required === 'growth' ? 'bg-brand-blue text-white' :
            'bg-white/20 text-white'
          }`}>
            {course?.tier_required} Access
          </span>
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair text-white mb-4 leading-tight" data-testid="course-title">
          {course?.title}
        </h1>
        <p className="text-base md:text-lg text-gray-300 mb-6 max-w-2xl font-light line-clamp-3">
          {course?.description}
        </p>
        <div className="flex items-center gap-4 flex-wrap">
          {hasAccess && curriculum?.length > 0 && curriculum[0].lessons.length > 0 && (
            <Button
              onClick={onStart}
              className="bg-white text-black hover:bg-gray-200 px-6 py-3 rounded-full font-semibold flex items-center gap-2 shadow-lg"
              data-testid="hero-start-btn"
            >
              <Play className="w-5 h-5 fill-current" />
              {stats?.completed_lessons > 0 ? 'Continue Watching' : 'Start Course'}
            </Button>
          )}
          {!hasAccess && course?.price > 0 && (
            <Button onClick={onPurchase} className="bg-brand-gold text-black hover:brightness-110 px-6 py-3 rounded-full font-semibold" data-testid="hero-purchase-btn">
              <Lock className="w-4 h-4 mr-2" /> Unlock for ${course.price}
            </Button>
          )}
          <div className="flex items-center gap-6 text-sm text-gray-300 ml-2">
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {stats?.total_duration || course?.total_duration_minutes || 0} min</span>
            <span className="flex items-center gap-1.5"><Video className="w-4 h-4" /> {stats?.total_lessons || 0} lessons</span>
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {course?.enrollment_count || 0} enrolled</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);
